import React, { useState } from "react";
import { Message, Table, useToaster } from "rsuite";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faSearch } from '@fortawesome/free-solid-svg-icons';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import { IApp, AppStatus } from "@bprotsyk/aso-core";
import { AppPlatform } from "constants/app-initial";
import { PanelService } from "network/panel/network";
import { AppListItem } from "./app-list-item";
import ReloadIcon from "@rsuite/icons/Reload";

const { Column, HeaderCell, Cell } = Table;

// Enum для типів директу
export enum EDirectType {
  KEITARO_OFFER = 'keitaroOffer',
  OFFER_DIRECT = 'offerDirect'
}

interface ISearchInput {
  search: string;
  setSearch: (value: string) => void;
}

const CustomInputGroup = ({ search, setSearch }: ISearchInput) => (
  <div className="lists-search" style={{ display: 'flex', alignItems: 'center' }}>
    <div style={{ position: 'relative', flex: 1 }}>
      <input
        type="text"
        placeholder="Пошук"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="lists-search-input"
      />
      <FontAwesomeIcon icon={faSearch} className="lists-search-icon" />
    </div>
  </div>
);

interface AppsTableProps {
  apps: IApp[];
  loading: boolean;
  onAppClick: (app: IApp) => void;
  onStatusChange: (appId: number, newStatus: AppStatus) => void;
  openDropdownId: number | null;
  dropdownPosition: { x: number; y: number } | null;
  onStatusCellClick: (appId: number, event: React.MouseEvent) => void;
  dropdownRefs: React.MutableRefObject<Map<number, HTMLDivElement>>;
  sortColumn: string;
  sortType: 'asc' | 'desc';
  onSort: (dataKey: string, sortType: 'asc' | 'desc' | undefined) => void;
  tableHeight: number;
  search: string;
  onSearchChange: (value: string) => void;
  onCreateApp: () => void;
  filters: {
    hideArchived: boolean;
    hideInDevelopment: boolean;
  };
  offerwallCount: number;
}

// Helper functions
// getIntegrationVersionText removed - integrationVersion field no longer exists

const isValidAndroidPackage = (bundle: string): boolean => {
  if (!bundle) return false;
  const androidPackageRegex = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;
  return androidPackageRegex.test(bundle);
};

const isGooglePlayUrl = (bundle: string): boolean => {
  try {
    const url = new URL(bundle);
    return url.hostname === 'play.google.com' && url.pathname.includes('/store/apps/details') && url.searchParams.has('id');
  } catch {
    return false;
  }
};

const getGooglePlayBundleId = (bundle: string): string => {
  if (!bundle) return '';
  if (isGooglePlayUrl(bundle)) {
    try {
      const url = new URL(bundle);
      return url.searchParams.get('id') || '';
    } catch {
      return '';
    }
  }
  return bundle.replace(/^id=/i, '').split(/[?#]/)[0].trim();
};

export const getAppDomainPath = (app: IApp): string => {
  const domain = String(app.domainParams?.name || '').trim().replace(/\/+$/, '');
  if (!domain) return 'Не вказано';

  const offerwallPath = String((app as any).offerwallParams?.path || '')
    .trim()
    .replace(/^\/+|\/+$/g, '');
  return offerwallPath ? `${domain}/${offerwallPath}` : domain;
};

export const getPdlEntryCampaign = (app: IApp) => {
  const entry = (app as any).offerwallParams?.entryCampaign;
  const campaignId = Number(entry?.campaignId || 0);
  const trackingUrl = String(entry?.trackingUrl || '').trim();
  if (!campaignId || !trackingUrl) return null;
  return {
    campaignId,
    campaignName: String(entry?.campaignName || ''),
    trackingUrl,
  };
};

const isGooglePlayReference = (bundle: string): boolean => {
  return isValidAndroidPackage(getGooglePlayBundleId(bundle)) || isGooglePlayUrl(bundle);
};

const getGooglePlayUrl = (bundle: string): string => {
  return `https://play.google.com/store/apps/details?id=${encodeURIComponent(getGooglePlayBundleId(bundle) || bundle)}`;
};

const getStatusColor = (status: AppStatus, isChanged: boolean = false): string => {
  if (isChanged) {
    return "#ffc107";
  }
  
  switch (status) {
    case AppStatus.ARCHIVED:
      return "#6c757d";
    case AppStatus.IN_DEVELOPMENT:
      return "#007bff";
    case AppStatus.READY:
      return "#28a745";
    case AppStatus.DISABLED:
      return "#ffc107"; // Yellow
    default:
      return "#6c757d";
  }
};

const getStatusText = (status: AppStatus): string => {
  switch (status) {
    case AppStatus.ARCHIVED:
      return "Архів";
    case AppStatus.IN_DEVELOPMENT:
      return "В розробці";
    case AppStatus.READY:
      return "Готовий";
    case AppStatus.DISABLED:
      return "Вимкнено";
    default:
      return "Невідомо";
  }
};

const getAvailableStatusTransitions = (currentStatus: AppStatus): AppStatus[] => {
  switch (currentStatus) {
    case AppStatus.ARCHIVED:
      return [AppStatus.IN_DEVELOPMENT, AppStatus.READY, AppStatus.DISABLED];
    case AppStatus.IN_DEVELOPMENT:
      return [AppStatus.ARCHIVED, AppStatus.READY, AppStatus.DISABLED];
    case AppStatus.READY:
      return [AppStatus.ARCHIVED, AppStatus.IN_DEVELOPMENT, AppStatus.DISABLED];
    case AppStatus.DISABLED:
      return [AppStatus.ARCHIVED, AppStatus.IN_DEVELOPMENT, AppStatus.READY];
    default:
      return [AppStatus.ARCHIVED, AppStatus.IN_DEVELOPMENT, AppStatus.READY, AppStatus.DISABLED];
  }
};

const getSortValue = (app: IApp, key: string): string | number => {
  switch (key) {
    case 'id':
      return app.id;
    case 'name':
      return app.name || '';
    case 'bundle':
      return getGooglePlayBundleId(app.bundle || '') || app.bundle || '';
    case 'status':
      return app.status;
    default:
      return '';
  }
};

const SyncCampaignsButton: React.FC = () => {
  const [syncing, setSyncing] = useState(false);
  const toaster = useToaster();

  const handleSync = async () => {
    if (!window.confirm('Актуалізувати всі ◈ кампанії? Це оновить потоки для всіх кампаній.')) return;
    setSyncing(true);
    try {
      const result = await PanelService.syncAppxCampaigns();
      toaster.push(
        <Message showIcon type="success" closable>{result.message}</Message>,
        { placement: 'topEnd', duration: 5000 }
      );
    } catch (e: any) {
      toaster.push(
        <Message showIcon type="error" closable>Помилка: {e?.message || 'Не вдалося'}</Message>,
        { placement: 'topEnd', duration: 5000 }
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <button
      className="lists-tab"
      onClick={handleSync}
      disabled={syncing}
      style={{ opacity: syncing ? 0.6 : 1 }}
    >
      <ReloadIcon style={{ marginRight: '6px', color: '#3498db' }} />
      {syncing ? 'Синхронізація...' : 'Актуалізувати кампанії'}
    </button>
  );
};

const AppsTable: React.FC<AppsTableProps> = ({
  apps,
  loading,
  onAppClick,
  onStatusChange,
  openDropdownId,
  dropdownPosition,
  onStatusCellClick,
  dropdownRefs,
  sortColumn,
  sortType,
  onSort,
  tableHeight,
  search,
  onSearchChange,
  onCreateApp,
  filters,
  offerwallCount
}) => {
  return (
    <div className="apps-table">
      <div className="apps-header">
        <div className="apps-header-content">
          <div className="apps-actions">
            <button
              className="lists-tab create-app-btn"
              onClick={onCreateApp}
            >
              Створити новий додаток
            </button>
            <span className="apps-separator" />
            <span
              className="lists-tab apps-mode-tab active"
              aria-current="page"
            >
              Offerwall <span>{offerwallCount}</span>
            </span>
            <span className="apps-separator" />
            <SyncCampaignsButton />
          </div>
          <div className="apps-search">
            <CustomInputGroup 
              search={search} 
              setSearch={onSearchChange} 
            />
          </div>
        </div>
      </div>

      <Table
        data={apps.filter(app => {
          // Фільтрація за статусом
          if (filters.hideArchived && app.status === AppStatus.ARCHIVED) return false;
          if (filters.hideInDevelopment && app.status === AppStatus.IN_DEVELOPMENT) return false;

          return true;
        }).sort((a, b) => {
          if (sortColumn && sortType) {
            const aValue = getSortValue(a, sortColumn);
            const bValue = getSortValue(b, sortColumn);
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              return sortType === 'asc' 
                ? aValue.localeCompare(bValue) 
                : bValue.localeCompare(aValue);
            }
            
            return sortType === 'asc' 
              ? (aValue > bValue ? 1 : -1)
              : (bValue > aValue ? 1 : -1);
          }
          
          return 0;
        })}
        loading={loading}
        bordered
        cellBordered
        virtualized
        height={tableHeight}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSort}
        onRowClick={(rowData: IApp) => onAppClick(rowData)}
        locale={{ loading: 'Завантаження...', emptyMessage: 'Дані відсутні' }}
      >
        <Column width={80} align="left" sortable>
          <HeaderCell>ID</HeaderCell>
          <Cell dataKey="id">
            {(rowData: IApp) => (
              <div 
                style={{ display: 'flex', alignItems: 'center', height: '100%' }}
                className="noselect"
              >
                <AppListItem app={rowData} onClick={onAppClick} />
              </div>
            )}
          </Cell>
        </Column>

        <Column width={70} align="center" sortable>
          <HeaderCell>OS</HeaderCell>
          <Cell dataKey="appPlatform">
            {(rowData: IApp) => {
              const platform = (rowData as any).appPlatform;
              if (!platform) return <span style={{ color: '#555', fontSize: '14px' }}>—</span>;
              return (
                <FontAwesomeIcon
                  icon={platform === AppPlatform.ANDROID ? faAndroid : faApple}
                  style={{ color: platform === AppPlatform.ANDROID ? '#3ddc84' : '#ffffff', fontSize: '16px' }}
                />
              );
            }}
          </Cell>
        </Column>

        <Column width={180} align="left">
          <HeaderCell>Гео</HeaderCell>
          <Cell>
            {(rowData: IApp) => {
              const geos = (rowData as any).geos as string[] | undefined;
              if (!geos || geos.length === 0) return <span style={{ color: '#555', fontSize: '12px' }}>—</span>;
              const toFlag = (code: string) => code.toUpperCase().split('').map((c: string) => String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0))).join('');
              return (
                <div className="noselect" style={{ fontSize: '13px', lineHeight: '20px', whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'hidden', maxHeight: '40px' }}>
                  {[...geos].sort().map((g) => toFlag(g)).join(' ')}
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Ім'я</HeaderCell>
          <Cell dataKey="name">
            {(rowData: IApp) => {
              const name = rowData.name || "Не вказано";
              
              return (
                <div className="name-cell noselect">
                  <span>{name}</span>
                  <FontAwesomeIcon 
                    icon={faCopy}
                    className="copy-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(name);
                    }}
                  />
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={1} align="left" sortable>
          <HeaderCell>Bundle</HeaderCell>
          <Cell dataKey="bundle">
            {(rowData: IApp) => {
              const bundle = rowData.bundle || "Не вказано";
              const displayBundle = getGooglePlayBundleId(bundle) || bundle;
              
              return (
                <div className="bundle-cell noselect">
                  {isGooglePlayReference(bundle) ? (
                    <a 
                      href={getGooglePlayUrl(bundle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bundle-link"
                      style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {displayBundle}
                    </a>
                  ) : (
                    <span>{displayBundle}</span>
                  )}
                  <FontAwesomeIcon 
                    icon={faCopy}
                    className="copy-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(displayBundle);
                    }}
                  />
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Домен</HeaderCell>
          <Cell dataKey="domainParams.name">
            {(rowData: IApp) => {
              const domainPath = getAppDomainPath(rowData);
              
              return (
                <div 
                  className="domain-cell noselect"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(domainPath);
                  }}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <span>{domainPath}</span>
                  <FontAwesomeIcon 
                    icon={faCopy}
                    className="copy-icon"
                  />
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={2} align="left">
          <HeaderCell>Кампанія</HeaderCell>
          <Cell>
            {(rowData: IApp) => {
              const campaign = (rowData as any).campaign;
              if (!campaign?.campaignId) return <span style={{ color: '#7f8c8d', fontSize: '12px' }}>—</span>;
              return (
                <div className="campaign-cell noselect" style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#95a5a6' }}>#{campaign.campaignId}</span>{' '}
                  <span>{campaign.campaignName || ''}</span>
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={3} align="left">
          <HeaderCell>Вхідна кампанія</HeaderCell>
          <Cell>
            {(rowData: IApp) => {
              const entry = getPdlEntryCampaign(rowData);
              if (!entry) return <span style={{ color: '#7f8c8d', fontSize: '12px' }}>—</span>;
              return (
                <div className="entry-campaign-cell noselect">
                  <div className="entry-campaign-name" title={entry.campaignName}>
                    <span>#{entry.campaignId}</span>{' '}
                    <span>{entry.campaignName}</span>
                  </div>
                  <div className="entry-campaign-link">
                    <a
                      href={entry.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={entry.trackingUrl}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {entry.trackingUrl}
                    </a>
                    <FontAwesomeIcon
                      icon={faCopy}
                      className="copy-icon"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigator.clipboard.writeText(entry.trackingUrl);
                      }}
                    />
                  </div>
                </div>
              );
            }}
          </Cell>
        </Column>

      </Table>

      {/* Global dropdown positioned outside table */}
      {openDropdownId !== null && dropdownPosition && (() => {
        const app = apps.find(a => a.id === openDropdownId);
        if (!app) return null;
        
        const currentStatus = app.status;
        const availableTransitions = getAvailableStatusTransitions(currentStatus);
        
        return (
          <div
            ref={(el) => {
              if (el) dropdownRefs.current.set(openDropdownId, el);
            }}
            className="status-dropdown"
            style={{
              position: 'absolute',
              left: dropdownPosition.x,
              top: dropdownPosition.y - 12,
              zIndex: 9999,
              backgroundColor: '#2c3e50',
              border: '1px solid #34495e',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              minWidth: '150px'
            }}
          >
            {/* Available transitions */}
            {availableTransitions
              .filter(status => status !== currentStatus)
              .map((status) => (
                <div
                  key={status}
                  onClick={() => onStatusChange(app.id, status)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: currentStatus === status ? '#34495e' : 'transparent',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (currentStatus !== status) {
                      e.currentTarget.style.backgroundColor = '#34495e';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentStatus !== status) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div 
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(status),
                      flexShrink: 0
                    }}
                  />
                  <span style={{ fontSize: '12px', color: '#ecf0f1' }}>
                    {getStatusText(status)}
                  </span>
                </div>
              ))}
          </div>
        );
      })()}
    </div>
  );
};

export default AppsTable;
